import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, setAccessToken, tryRefresh } from "../api/client.js";
import type {
  LoginResult,
  MfaRequired,
  PasswordExpired,
  PublicUser,
  Permission,
  LicenseStatusLite,
} from "../types.js";

interface LoginOutcome {
  mfaRequired: boolean;
  passwordExpired?: boolean;
  challengeToken?: string;
}

interface AuthState {
  user: PublicUser | null;
  license: LicenseStatusLite | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /**
   * Self-service sign-up. Deliberately does NOT start a session: the API answers
   * identically whether or not the address was already registered (anti user-
   * enumeration), so there are no tokens to consume. The caller sends the user
   * to sign-in with a "check your email" notice.
   */
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  verifyMfa: (challengeToken: string, code: string) => Promise<void>;
  completeExpiredPassword: (challengeToken: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (p: Permission) => boolean;
  refreshMe: () => Promise<void>;
  refreshLicense: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [license, setLicense] = useState<LicenseStatusLite | null>(null);
  const [loading, setLoading] = useState(true);

  const loadLicense = async () => {
    try {
      setLicense(await api<LicenseStatusLite>("/license"));
    } catch {
      setLicense(null);
    }
  };

  // On load, try to silently restore a session from the refresh cookie.
  //
  // This MUST run once and go through the client's coalescing tryRefresh(): two
  // concurrent /auth/refresh calls with the same cookie trip refresh-token
  // reuse-detection, which revokes the whole session family and logs the user
  // out on reload. React StrictMode double-invokes effects in dev, so we both
  // guard with a ref (run the bootstrap once per mount) and route through
  // tryRefresh() (any concurrent refresh dedupes to a single request).
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    void (async () => {
      try {
        if (await tryRefresh()) {
          setUser(await api<PublicUser>("/auth/me"));
          await loadLicense();
        } else {
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshMe = async () => {
    const me = await api<PublicUser>("/auth/me");
    setUser(me);
  };

  const login: AuthState["login"] = async (email, password) => {
    const res = await api<LoginResult | MfaRequired | PasswordExpired>("/auth/login", {
      method: "POST",
      body: { email, password },
      noRetry: true,
    });
    if ("mfaRequired" in res) return { mfaRequired: true, challengeToken: res.challengeToken };
    if ("passwordExpired" in res)
      return { mfaRequired: false, passwordExpired: true, challengeToken: res.challengeToken };
    setAccessToken(res.tokens.accessToken);
    setUser(res.user);
    await loadLicense();
    return { mfaRequired: false };
  };

  const register: AuthState["register"] = async (email, password, displayName) => {
    // 202 { pending: true } — no tokens by design (see the interface comment).
    await api<{ pending: boolean }>("/auth/register", {
      method: "POST",
      body: { email, password, ...(displayName ? { displayName } : {}) },
      noRetry: true,
    });
  };

  const completeExpiredPassword: AuthState["completeExpiredPassword"] = async (
    challengeToken,
    newPassword,
  ) => {
    await api<void>("/auth/password/expired", {
      method: "POST",
      body: { challengeToken, newPassword },
      noRetry: true,
    });
    // Password changed + all sessions revoked — the user logs in again fresh.
  };

  const verifyMfa: AuthState["verifyMfa"] = async (challengeToken, code) => {
    const res = await api<LoginResult>("/auth/mfa/verify", {
      method: "POST",
      body: { challengeToken, code },
      noRetry: true,
    });
    setAccessToken(res.tokens.accessToken);
    setUser(res.user);
    await loadLicense();
  };

  const logout = async () => {
    try {
      await api<void>("/auth/logout", { method: "POST", noRetry: true });
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  };

  const value = useMemo<AuthState>(
    () => ({
      user,
      license,
      loading,
      login,
      register,
      verifyMfa,
      completeExpiredPassword,
      logout,
      refreshMe,
      refreshLicense: loadLicense,
      hasPermission: (p) => !!user && user.permissions.includes(p),
    }),
    [user, license, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
